// The cabinet's data layer. One client for the whole app, same API either way:
//   production  gcp.js — Firebase Auth + cabinet-api on Cloud SQL / Cloud Storage,
//               configured by cabinet/config.json
//   development the same calls on PGlite in the browser (devdb.js) — with no
//               config.json, or with ?dev=1 in the address
// Every read and write goes through Row Level Security: the database decides
// what a student, a teacher or the admin may see (cabinet/db/migrations).
//
// Queries stay simple (one table or view at a time, no embedded joins) so the
// dev client can run them too; joins live in SQL views and functions.

let client = null;
let mode = null;
let meCache = null;

export async function init(onStep = () => {}) {
    if (client) return client;
    let cfg = null;
    const forceDev = new URLSearchParams(location.search).has('dev');
    if (!forceDev) {
        // Preloaded by cabinet.html (the proxy serves it no-cache: always fresh).
        try { const r = await fetch('/cabinet/config.json'); if (r.ok) cfg = await r.json(); } catch {}
    }
    if (cfg?.firebase?.apiKey) {
        const { createGcpClient } = await import('./gcp.js');
        client = await createGcpClient(cfg);
        mode = 'gcp';
    } else {
        const dev = await import('./devdb.js');
        await dev.devDb(onStep);
        client = dev.createDevClient();
        mode = 'dev';
    }
    client.auth.onAuthStateChange(() => { meCache = null; worldCache = null; courseCache = null; signIns++; });
    return client;
}
export const db = () => client;
// What a page kept from an earlier read is current while this stays the same: it
// moves with every write of this tab (gcp.js / devdb.js `writes`) and every sign-in change.
let signIns = 0;
export const dataVersion = () => `${signIns}:${client?.writes ?? 0}`;
// A read the next page of this visit may reuse: kept while dataVersion stands and
// for at most `ttl`, as others change things meanwhile (students hand in). Each
// round trip costs 200-400 ms from a browser (2026-10-14). A failure is not kept.
const keptReads = new Map();
export function kept(key, read, ttl = 60e3) {
    const v = dataVersion(), hit = keptReads.get(key);
    if (hit && hit.v === v && Date.now() - hit.at < ttl) return hit.p;
    const p = read().catch((e) => { if (keptReads.get(key)?.p === p) keptReads.delete(key); throw e; });
    keptReads.set(key, { v, at: Date.now(), p });
    return p;
}
export const isDev = () => mode === 'dev';
// Test accounts to sign in as: the in-browser demo, or staging (config.json `demo`).
export const hasDemo = () => isDev() || !!client?.auth?.demoAccounts;
export async function demoAccounts() {
    if (isDev()) return (await import('./devdb.js')).devAccounts();
    return client.auth.demoAccounts();
}
export async function signInDemo(email) {
    const r = isDev() ? await signInEmail(email) : await client.auth.demoSignIn(email);
    meCache = null;
    return r;
}

// Throwing wrapper: every caller wants the data or a visible error.
export async function q(p) {
    const { data, error, count } = await p;
    if (error) throw Object.assign(new Error(error.message), { code: error.code });
    return count != null && data == null ? count : data;
}
const ids = (rows, k = 'id') => [...new Set((rows || []).map(r => r[k]).filter(Boolean))];

// ── Who am I ─────────────────────────────────────────────────────────────
export async function me(force = false) {
    if (meCache && !force) return meCache;
    const { data: { user } } = await client.auth.getUser();
    if (!user) return (meCache = null);
    // Every role's pages read the curriculum: it loads beside the profile, not after it
    // (both Year 1 subjects; yearOne() itself would wait for me()).
    yearOneSubjects().catch(() => {});
    // A student may be in several groups at once (one per предмет). The groups
    // are asked for with the profile (one round trip, not two); others drop them.
    // Production's /me brings both already (takeBoot), once per sign-in check.
    const myGroups = () => q(client.from('student_track').select('*').eq('student_id', user.id));
    const boot = client.auth.takeBoot?.(user.id);
    let [profile, groups] = boot ? [boot.profile, boot.groups]
        : await Promise.all([q(client.from('profiles').select('*').eq('id', user.id).maybeSingle()), myGroups()]);
    if (!profile) {
        await client.rpc('claim_invited_profile');
        [profile, groups] = await Promise.all([q(client.from('profiles').select('*').eq('id', user.id).maybeSingle()), myGroups()]);
    }
    // The groups' current courses came with them (/me): their topics load now,
    // beside the world, not a round trip after it (world() reads them).
    if (profile?.role === 'student') boot?.courses?.forEach(id => courseCurriculum(id).catch(() => {}));
    if (!profile) return (meCache = { user, profile: null });
    if (profile.role !== 'student') groups = [];
    return (meCache = { user, profile, role: profile.role, track: groups[0]?.track || null, group: groups[0] || null, groups });
}
export function forgetMe() { meCache = null; if (courseCache) courseCache.mine = null; }   // a new group may mean another Year 1 subject

export async function signInEmail(email) {
    const redirect = location.origin + '/cabinet/';
    return client.auth.signInWithOtp({ email, options: { emailRedirectTo: redirect, shouldCreateUser: true } });
}
export const signInGoogle = () => client.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: location.origin + '/cabinet/' } });
// An email link opened on another device waits for its address; a failed link
// or Google sign-in leaves a message for the sign-in page. (Production only.)
export const pendingEmailLink = () => !!client.auth.pendingLink?.();
export const signInProblem = () => client.auth.takeError?.() || null;
export async function signOut() { await client.auth.signOut(); meCache = null; worldCache = null; courseCache = null; }

// ── The waiting list (20261014235000_waitlist.sql, pages/waitlist.js) ─────
// Signed in with neither an account nor an invitation (known after me()):
// { email, name, google } or null.
export const stranger = () => client.auth.stranger?.() || null;
// get · join(w) · leave · byToken(t) · leaveByToken(t): each answers its body or { error }.
export const waitlist = () => client.waitlist;
// Admins and editors (RLS): the whole list, who has waited longest first.
export const waitlistAll = () => q(client.from('waitlist').select('id, email, full_name, role, grade, school, city, created_at').order('created_at'));

// Platform use, for the admin's analytics. Fire-and-forget; never blocks a page.
let lastPath = '';
export function logVisit(path) {
    if (!meCache?.user || path === lastPath) return;
    lastPath = path;
    client.from('activity').insert({ user_id: meCache.user.id, kind: 'visit', path }).then(() => {}, () => {});
}

// ── Curriculum ───────────────────────────────────────────────────────────
// Year 1 of olympiad maths is two subjects since 2026-10-11 (migration
// 20261011000000_split_subjects.sql), each with its own copy of the lessons:
// om-101 (AI-taught, groups.track 'ai') and om-102 (teacher-taught). Both are
// `open_to_applicants`: an applicant with no group takes the entrance test on them.
const TRACK_SUBJECT = { ai: 'om-101', teacher: 'om-102' };
// One request in flight serves every caller (me() starts it early); a failure is not kept.
let courseCache = null;   // { courses: Promise<[open courses]>, byId: Map<course id, Promise<curriculum>>, mine: Promise }
const curricula = () => (courseCache ||= { byId: new Map(), mine: null,
    courses: q(client.from('courses').select('*').eq('open_to_applicants', true).order('slug')).catch((e) => { courseCache = null; throw e; }) });
// One course and its topics, by id (any course the caller may read).
export function courseCurriculum(courseId) {
    const C = curricula();
    if (!C.byId.has(courseId)) C.byId.set(courseId, (async () => {
        const [course, topics] = await Promise.all([
            q(client.from('courses').select('*').eq('id', courseId).maybeSingle()),
            // An archived lesson is gone for everyone in the cabinet (students cannot read it at all).
            q(client.from('course_topics').select('*').eq('course_id', courseId).is('archived_at', null).order('index_no')),
        ]);
        return { course, topics, byCode: Object.fromEntries(topics.map(t => [t.code, t])), byId: Object.fromEntries(topics.map(t => [t.id, t])) };
    })().catch((e) => { C.byId.delete(courseId); throw e; }));
    return C.byId.get(courseId);
}
// The Year 1 subjects (om-101, om-102), each with its topics.
export async function yearOneSubjects() {
    return Promise.all((await curricula().courses).map(c => courseCurriculum(c.id)));
}
// The Year 1 subject for a group track ('ai' → om-101, 'teacher' → om-102).
export async function yearOneForTrack(track) {
    const cs = await curricula().courses;
    const c = cs.find(x => x.slug === (TRACK_SUBJECT[track] || TRACK_SUBJECT.teacher)) || cs[0];
    return c ? courseCurriculum(c.id) : { course: null, topics: [], byCode: {}, byId: {} };
}
// A group's current course with its topics.
export async function groupCurriculum(groupId) {
    const gc = await q(client.from('group_courses').select('course_id').eq('group_id', groupId).is('ends_on', null).maybeSingle());
    return gc ? courseCurriculum(gc.course_id) : yearOneForTrack('teacher');
}
// The caller's own Year 1 subject (or one by id). A student's is the Year 1 course
// one of their groups is on. Without one (an applicant before allocation: the
// entrance test is saved on 102, where the split left applicants' results) and
// for staff, om-102.
export async function yearOne(courseId) {
    if (courseId) return courseCurriculum(courseId);
    const C = curricula();
    return (C.mine ||= (async () => {
        const [cs, m] = await Promise.all([C.courses, me()]);
        const gids = (m?.groups || []).map(g => g.group_id);
        const gcs = gids.length ? await q(client.from('group_courses').select('course_id').in('group_id', gids).is('ends_on', null)) : [];
        const mine = cs.find(c => gcs.some(g => g.course_id === c.id));
        return mine ? courseCurriculum(mine.id) : yearOneForTrack('teacher');
    })().catch((e) => { C.mine = null; throw e; }));
}

// ── Student ──────────────────────────────────────────────────────────────
// Everything a student page needs, in the shape the views use.
export async function studentState() {
    const m = await me();
    const uid = m.user.id;
    const { topics, byId } = await yearOne();
    const [placement, levels, statuses, plan, done, attempts, checkins, commits, application] = await Promise.all([
        q(client.from('placements').select('*').eq('student_id', uid).order('started_at', { ascending: false }).limit(1).maybeSingle()),
        q(client.from('level_history').select('*').eq('student_id', uid).order('created_at')),
        q(client.from('topic_status').select('*').eq('student_id', uid)),
        q(client.from('study_plans').select('*').eq('student_id', uid).maybeSingle()),
        q(client.from('plan_done').select('*').eq('student_id', uid)),
        q(client.from('practice_attempts').select('*').eq('student_id', uid).order('created_at')),
        q(client.from('checkins').select('*').eq('student_id', uid).order('month')),
        q(client.from('commitments').select('*').eq('student_id', uid)),
        q(client.from('applications').select('*').eq('student_id', uid).maybeSingle()),
    ]);
    const consent = await q(client.from('parental_consents').select('*').eq('student_id', uid).maybeSingle());
    const status = {};
    statuses.forEach(r => { const t = byId[r.topic_id]; if (t) status[t.code] = r.status; });
    const att = {};
    attempts.forEach(a => (att[a.source_slug] ||= []).push(a));
    return {
        uid, profile: m.profile, track: m.track, group: m.group, topics,
        placement, application,
        theta: levels.length ? +levels.at(-1).theta : null,
        thetaHistory: levels.map(l => ({ at: Date.parse(l.created_at), theta: +l.theta, label: l.label || l.source })),
        status, plan: plan?.plan || null, planRow: plan,
        done: Object.fromEntries(done.map(d => [d.task_id, Date.parse(d.done_at)])),
        attempts: att, checkins,
        commit: Object.fromEntries(commits.map(c => [String(c.week_start).slice(0, 10), c.days])),
        consent: consent?.given_at ? consent : null,
        commitRows: commits.map(c => ({ ...c, week_start: String(c.week_start).slice(0, 10) })).sort((a, b) => a.week_start.localeCompare(b.week_start)),
    };
}

// The student's tree in one go: their groups (one per предмет) with the course,
// the teachers' names, every lesson, the homework (state from my_homework, the
// lesson it belongs to from assignments), preparation and flashcard progress.
// Lessons are read column by column: `notes` is the teacher's.
// One copy serves page after page for 30 s (it was 11 queries on every tap); a
// write that changes it, a sign-in change or the tab coming back drops it.
let worldCache = null;
export function worldChanged() { worldCache = null; }
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') worldCache = null; });
export async function studentWorld() {
    const m = await me();
    const uid = m.user.id;
    if (worldCache?.uid === uid && Date.now() - worldCache.at < 30e3) return worldCache.p;
    const p = loadWorld(m, uid).catch((e) => { worldCache = null; throw e; });
    worldCache = { uid, at: Date.now(), p };
    return p;
}
async function loadWorld(m, uid) {
    const gids = (m.groups || []).map(g => g.group_id);
    const none = Promise.resolve([]);
    // Every course comes in the same batch (any signed-in person reads them, and
    // there are a handful): asking by the groups' course ids took a round trip more.
    const [groups, gcourses, allCourses, lessons, homework, assignments, teachers, prep, cards, consent, level] = await Promise.all([
        gids.length ? q(client.from('groups').select('id,name,track,zoom_url,miro_url,telegram_url').in('id', gids)) : none,
        gids.length ? q(client.from('group_courses').select('group_id,course_id').in('group_id', gids).is('ends_on', null)) : none,
        gids.length ? q(client.from('courses').select('id,name,slug')) : none,
        gids.length ? q(client.from('lessons').select('id,group_id,index_no,title,topic,topic_id,starts_at,duration_min,zoom_url,miro_url,recording_url,status,materials_open_at,grades_open_at').in('group_id', gids).order('starts_at')) : none,
        q(client.from('my_homework').select('*')),
        gids.length ? q(client.from('assignments').select('id,group_id,lesson_id').in('group_id', gids)) : none,
        gids.length ? q(client.rpc('my_teachers')) : none,
        q(client.from('prep_progress').select('topic_id,explanation,game').eq('student_id', uid)),
        q(client.from('flashcard_state').select('card_key,box,due_at,reviews').eq('student_id', uid)),
        q(client.from('parental_consents').select('given_at,scope_photos,scope_ai').eq('student_id', uid).maybeSingle()),
        q(client.from('level_history').select('theta').eq('student_id', uid).order('created_at', { ascending: false }).limit(1)),
    ]);
    const courseIds = new Set(gcourses.map(c => c.course_id));
    const courses = allCourses.filter(c => courseIds.has(c.id));
    return { groups, courses, groupCourse: Object.fromEntries(gcourses.map(c => [c.group_id, c.course_id])), lessons, homework, assignments,
        teachers: teachers || [], prep, cards, consent: consent?.given_at ? consent : null, theta: level[0] ? +level[0].theta : null };
}
// A lesson's blocks (a video and/or text, then the problem under it), written once on the topic.
// The answers and hints stay in the database: answer_block() checks an answer
// and returns right/wrong per field; take_block_hint() gives the hint on request.
// Live blocks only, as my_topic_blocks (staff read archived ones too, but not on the lesson page).
export async function topicBlocks(topicId) {
    return q(client.from('topic_blocks').select('id,topic_id,n,title,video_url,video_seconds,body,statement,problem_title,inputs').eq('topic_id', topicId).is('archived_at', null).order('n').order('created_at'));
}
export async function myBlockAttempts(blockIds) {
    if (!blockIds.length) return [];
    return q(client.from('block_attempts').select('block_id,answers,parts,correct,created_at').eq('student_id', (await me()).user.id).in('block_id', blockIds).order('created_at'));
}
// A lesson's blocks with the caller's answers, watched videos and hints in one
// call (my_topic_blocks, migration 20261011000000): { blocks, attempts, views,
// hints }. A database without the function yet: the four reads, as before.
export async function lessonBlocks(topicId) {
    const { data, error } = await client.rpc('my_topic_blocks', { p_topic: topicId });
    if (!error && data?.blocks) return data;
    const blocks = await topicBlocks(topicId);
    const ids = blocks.map(x => x.id);
    const [attempts, views, hints] = await Promise.all([myBlockAttempts(ids), myBlockViews(ids), blockHints(topicId).catch(() => [])]);
    return { blocks, attempts, views, hints };
}
// The same, found by the lesson's address (group, lesson number) and asked for
// with the student's world instead of after it (my_lesson_blocks, migration
// 20261011000600): { topic_id, blocks, attempts, views, hints }, or null. Its
// videos' links are signed at once, in one call (lessonVideoUrl takes them).
export async function lessonBlocksAt(groupId, n) {
    const { data, error } = await client.rpc('my_lesson_blocks', { p_group: groupId, p_n: n });
    if (error || !data?.blocks) return null;
    presignVideos(data.blocks.map(b => storedVideo(b.video_url)).filter(Boolean));
    return data;
}
// A block's problem opens once its video is watched; watch_block() records it.
export async function myBlockViews(blockIds) {
    if (!blockIds.length) return [];
    return q(client.from('block_views').select('block_id').eq('student_id', (await me()).user.id).in('block_id', blockIds));
}
export async function watchBlock(blockId) { return q(client.rpc('watch_block', { p_block: blockId })); }
// A block's video in the cabinet's private bucket ('storage:lesson-videos/<path>',
// migration *_lesson_videos.sql) → a link for an hour, or null. Only whoever may
// open a block showing the video gets one; the player asks again when it runs out.
// Quality '360p' is the copy for a slow connection beside it: <stem>.360.mp4
// (tools/cabinet/encode_video.py).
export const storedVideo = (v) => (String(v || '').match(/^storage:lesson-videos\/(.+)$/) || [])[1] || null;
// Links signed ahead (presignVideos), each served once and only while fresh: the
// player asks again for a new one when a link runs out.
const presigned = new Map();   // path → { at, url: Promise<string|null> }
const PRESIGN_FRESH = 30 * 60e3;
function presignVideos(paths) {
    const bucket = client.storage.from('lesson-videos');
    const list = [...new Set(paths)].filter(p => !(Date.now() - (presigned.get(p)?.at ?? -Infinity) < PRESIGN_FRESH));
    if (!list.length || !bucket.createSignedUrls) return;   // the in-browser dev database signs one at a time
    const all = bucket.createSignedUrls(list, 3600).then(r => r?.data || [], () => []);
    list.forEach(p => presigned.set(p, { at: Date.now(), url: all.then(d => d.find(x => x.path === p)?.signedUrl || null) }));
}
export async function lessonVideoUrl(path, quality) {
    const pre = quality !== '360p' ? presigned.get(path) : null;
    if (pre) presigned.delete(path);
    if (quality === '360p') path = path.replace(/\.mp4$/, '.360.mp4');
    // The bucket's connection opens while the link is signed: its handshake is
    // two round trips the video would otherwise wait for (a second or two on a
    // slow phone). Added afresh each time, as a browser drops an idle one.
    document.querySelector('link[data-warm="video"]')?.remove();
    document.head.append(Object.assign(document.createElement('link'), { rel: 'preconnect', href: 'https://storage.googleapis.com' }));
    document.head.lastElementChild.dataset.warm = 'video';
    const early = pre && Date.now() - pre.at < PRESIGN_FRESH ? await pre.url : null;
    if (early) return early;
    const { data } = await client.storage.from('lesson-videos').createSignedUrl(path, 3600);
    return data?.signedUrl || null;
}
export async function answerBlock(blockId, answers) { return q(client.rpc('answer_block', { p_block: blockId, p_answers: JSON.stringify(answers) })); }
// The topic's blocks that have a hint, with its text where this student took it.
export async function blockHints(topicId) { return q(client.rpc('block_hints', { p_topic: topicId })) || []; }
export async function takeBlockHint(blockId) { return q(client.rpc('take_block_hint', { p_block: blockId })); }
export async function myAttempts(slugs) {
    if (!slugs.length) return [];
    return q(client.from('practice_attempts').select('source_slug,result,created_at').eq('student_id', (await me()).user.id).in('source_slug', slugs));
}

export async function savePlacementStart(questionnaire) {
    logEvent('survey_done', { grade: questionnaire.grade, experience: questionnaire.experience });
    const uid = (await me()).user.id;
    await q(client.from('profiles').update({ preferred_name: questionnaire.nick, grade: questionnaire.grade }).eq('id', uid));
    const row = await q(client.from('placements').insert({ student_id: uid, answers: { questionnaire } }).select('*').single());
    forgetMe();
    return row;
}
export async function savePlacementProgress(id, state) {
    await q(client.from('placements').update({ answers: state }).eq('id', id));
}
export async function finishPlacement(id, result, state) {
    logEvent('placement_done', { minutes: result.minutes, level: Math.round(result.theta) });
    const uid = (await me()).user.id;
    const { byCode } = await yearOne();
    await q(client.from('placements').update({ finished_at: new Date().toISOString(), theta: result.theta.toFixed(2), minutes: result.minutes, calibration: result.calibration, answers: state }).eq('id', id));
    await q(client.from('level_history').insert({ student_id: uid, theta: result.theta.toFixed(2), source: 'placement', label: 'Вхідний тест' }));
    const statusRows = Object.entries(result.status).filter(([c]) => byCode[c])
        .map(([code, status]) => ({ student_id: uid, topic_id: byCode[code].id, status, source: 'placement' }));
    if (statusRows.length) await q(client.from('topic_status').upsert(statusRows, { onConflict: 'student_id,topic_id' }));
    // The application to the programme (the admin allocates it to a track).
    const cohort = await q(client.from('cohorts').select('id').order('created_at', { ascending: false }).limit(1).maybeSingle());
    if (cohort) await client.from('applications').insert({ cohort_id: cohort.id, student_id: uid, placement_id: id });
}
export async function addLevel(theta, source, label) {
    await q(client.from('level_history').insert({ student_id: (await me()).user.id, theta: Math.max(1, Math.min(20, theta)).toFixed(2), source, label }));
}
export async function setTopicStatus(code, status, source) {
    const { byCode } = await yearOne();
    if (!byCode[code]) return;
    await q(client.from('topic_status').upsert({ student_id: (await me()).user.id, topic_id: byCode[code].id, status, source, updated_at: new Date().toISOString() }, { onConflict: 'student_id,topic_id' }));
}
export async function savePlan(plan, settings, { keepDone = false } = {}) {
    logEvent(keepDone ? 'plan_replanned' : 'plan_created', { weeks: plan.length, hours: settings?.hours });
    const uid = (await me()).user.id;
    const { course } = await yearOne();
    await q(client.from('study_plans').upsert({ student_id: uid, course_id: course?.id, plan, length: plan.length, hours: settings.hours,
        weeks_setting: String(settings.weeks), updated_at: new Date().toISOString() }, { onConflict: 'student_id' }));
    if (!keepDone) await q(client.from('plan_done').delete().eq('student_id', uid));
}
export async function markDone(taskId) {
    await client.from('plan_done').upsert({ student_id: (await me()).user.id, task_id: taskId }, { onConflict: 'student_id,task_id' });
}
export async function logAttempt(a) {
    logEvent('problem_attempt', { result: a.result, hints: a.hints || 0, topic: a.unit || null });
    const { byCode } = await yearOne();
    return q(client.from('practice_attempts').insert({ student_id: (await me()).user.id, source_slug: a.slug, topic_id: a.unit ? byCode[a.unit]?.id : null,
        task_id: a.taskId || null, result: a.result, answer: a.answer || null, work: a.work || null, hints: a.hints || 0, ai_verdict: a.aiVerdict || null }).select('*').single());
}
export async function claimModule(week) {
    const r = await q(client.rpc('claim_module_ap', { p_week: week }));
    return Array.isArray(r) ? r[0] : r;
}
export async function logTutor(slug, role, body, level, kind) {
    if (role === 'student' || kind === 'hint') logEvent(kind === 'hint' ? 'tutor_hint' : 'tutor_question', { level });
    await client.from('tutor_messages').insert({ student_id: (await me()).user.id, source_slug: slug, role, body, level, kind });
}
export async function tutorHistory(slug) {
    return q(client.from('tutor_messages').select('*').eq('student_id', (await me()).user.id).eq('source_slug', slug).order('created_at'));
}
export async function saveCheckin(row) {
    if (row.finished_at) logEvent('checkin_done', { month: row.month, solved: row.solved, total: row.total });
    return q(client.from('checkins').upsert({ ...row, student_id: (await me()).user.id }, { onConflict: 'student_id,month' }).select('*').single());
}
export async function saveCommitment(weekStart, days, { at_time = null, cue = null } = {}) {
    logEvent('commitment_set', { days: days.length, if_then: !!(at_time || cue) });
    await q(client.from('commitments').upsert({ student_id: (await me()).user.id, week_start: weekStart, days, at_time, cue }, { onConflict: 'student_id,week_start' }));
}
// ── Parental consent ──────────────────────────────────────────────────────
export async function myConsent() {
    return q(client.from('parental_consents').select('*').eq('student_id', (await me()).user.id).maybeSingle());
}
export async function consentLink() {
    let c = await myConsent();
    if (!c) c = await q(client.from('parental_consents').insert({ student_id: (await me()).user.id }).select('*').single());
    return `${location.origin}/cabinet/consent/${c.token}`;
}
export async function consentInfo(token) {
    const r = await q(client.rpc('consent_info', { p_token: token }));
    return Array.isArray(r) ? r[0] || null : r;
}
export async function giveConsent(token, f) {
    return q(client.rpc('give_consent', { p_token: token, p_name: f.name, p_relation: f.relation, p_email: f.email, p_phone: f.phone, p_data: f.data, p_photos: f.photos, p_ai: f.ai }));
}
// ── Product events (first-party; no third-party tracking of minors) ─────────
export function logEvent(name, props = {}) {
    me().then(m => m && client.from('product_events').insert({ user_id: m.user.id, name, props })).catch(() => {});
}
export async function myGroupGoal() {
    const r = await q(client.rpc('my_group_goal'));
    return Array.isArray(r) ? r[0] || null : r;
}
// Recognition the teacher gave («красивий розв'язок»), newest first.
export async function myFeatured() {
    const uid = (await me()).user.id;
    const subs = await q(client.from('submissions').select('id,assignment_id').eq('student_id', uid));
    if (!subs.length) return [];
    const marks = await q(client.from('problem_marks').select('submission_id,problem_id,comment,updated_at').eq('featured', true).in('submission_id', subs.map(x => x.id)));
    if (!marks.length) return [];
    const as = await q(client.from('assignments').select('id,title').in('id', [...new Set(subs.map(x => x.assignment_id))]));
    const ps = await q(client.from('problems').select('id,index_no').in('id', marks.map(m => m.problem_id)));
    return marks.map(m => ({ ...m, title: as.find(a => a.id === subs.find(x => x.id === m.submission_id)?.assignment_id)?.title || '', index_no: ps.find(p => p.id === m.problem_id)?.index_no })).sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
}
export async function updateProfile(fields) {
    await q(client.from('profiles').update(fields).eq('id', (await me()).user.id));
    forgetMe();
}

// ── Profile picture ───────────────────────────────────────────────────────
// profiles.avatar_url is either a path in the private «avatars» bucket
// (<uid>/avatar-<ms>.jpg, uploaded here) or an https URL: the Google picture
// that the first Google sign-in stored (cabinet-api ensure_user → handle_new_user).
// A stored photo is shown through a signed URL, kept in memory until shortly
// before it expires.
//
// The public site's header (localStorage `apex.me`, written by app.js) needs a
// picture that does not expire: a 72px JPEG data: URL (avatarMarker). The
// signed URL points at storage.googleapis.com, which sends no CORS headers, so
// a canvas could not read it: the small copy is made from the file itself at
// upload time and kept in localStorage `apex.me.photo` = {path, data}. On
// another device it is re-made from the signed URL where the image may be read
// (dev: the "signed URL" is a data: URL, so always), else there is none and the
// header shows initials.
const AVATARS = 'avatars';
const AVATAR_PX = 256, AVATAR_Q = 0.85, MARK_PX = 72, MARK_Q = 0.8;
const AVATAR_MAX = 2 * 1024 * 1024;          // the bucket's limit
const PHOTO_KEY = 'apex.me.photo';
const BAD_PHOTO = 'Не вдалося відкрити це фото. Спробуй інше (JPEG або PNG).';
const storedAvatar = (v) => typeof v === 'string' && v !== '' && !/^[a-z]+:/i.test(v);
const outsideAvatar = (v) => typeof v === 'string' && /^(https:|data:image\/)/i.test(v) ? v : null;
const avatarMarks = new Map();               // path → Promise<string|null>

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const obj = typeof src === 'string' ? null : URL.createObjectURL(src);
        if (!obj && /^https?:/i.test(src)) img.crossOrigin = 'anonymous';
        const done = () => { if (obj) URL.revokeObjectURL(obj); };
        img.onload = () => { done(); resolve(img); };
        img.onerror = () => { done(); reject(new Error(BAD_PHOTO)); };
        img.src = obj || src;
    });
}
// Centre square, halved step by step down to `px` (one big step looks rough),
// on white (a transparent PNG would turn black in a JPEG).
function squareCanvas(img, px) {
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height, s = Math.min(w, h);
    if (!s) throw new Error(BAD_PHOTO);
    const out = Math.min(px, s);
    let src = img, sx = (w - s) / 2, sy = (h - s) / 2, size = s;
    for (;;) {
        const next = size / 2 > out ? Math.round(size / 2) : out;
        const c = document.createElement('canvas');
        c.width = c.height = next;
        const g = c.getContext('2d');
        g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
        if (next === out) { g.fillStyle = '#fff'; g.fillRect(0, 0, next, next); }
        g.drawImage(src, sx, sy, size, size, 0, 0, next, next);
        if (next === out) return c;
        src = c; sx = sy = 0; size = next;
    }
}
const jpegBlob = (c, quality) => new Promise((resolve, reject) => c.toBlob(b => b ? resolve(b) : reject(new Error(BAD_PHOTO)), 'image/jpeg', quality));
const keptMark = (path) => { try { const k = JSON.parse(localStorage.getItem(PHOTO_KEY) || 'null'); return k?.path === path && /^data:image\//.test(k.data || '') ? k.data : null; } catch { return null; } };
const keepMark = (path, data) => { try { localStorage.setItem(PHOTO_KEY, JSON.stringify({ path, data })); } catch {} };
const dropMark = () => { try { localStorage.removeItem(PHOTO_KEY); } catch {} };

// A stored photo (photoUrl: a blob: URL), the Google picture, or null.
export function avatarUrl(profile) {
    const v = profile?.avatar_url;
    if (!storedAvatar(v)) return Promise.resolve(outsideAvatar(v));
    return photoUrl(AVATARS, v);
}
// For the public site's marker: a small data: URL of a stored photo, the
// Google https picture, or null. Never throws.
export async function avatarMarker(profile) {
    const v = profile?.avatar_url;
    if (!storedAvatar(v)) return outsideAvatar(v);
    const kept = keptMark(v);
    if (kept) return kept;
    if (!avatarMarks.has(v)) avatarMarks.set(v, (async () => {
        try {
            const url = await avatarUrl(profile);
            if (!url) return null;
            const data = squareCanvas(await loadImage(url), MARK_PX).toDataURL('image/jpeg', MARK_Q);   // throws on a tainted canvas
            keepMark(v, data);
            return data;
        } catch { return null; }
    })());
    return avatarMarks.get(v);
}
// The photo the person picked → a 256px square JPEG in the bucket and on the
// profile; the previous file goes (best effort). Returns a URL to show now.
export async function uploadAvatar(file) {
    const m = await me();
    const uid = m.user.id, prev = m.profile?.avatar_url;
    const c = squareCanvas(await loadImage(file), AVATAR_PX);
    const blob = await jpegBlob(c, AVATAR_Q);
    if (blob.size > AVATAR_MAX) throw new Error('Фото завелике. Спробуй інше.');
    const path = `${uid}/avatar-${Date.now()}.jpg`;
    const { error } = await client.storage.from(AVATARS).upload(path, blob, { contentType: 'image/jpeg', upsert: true });
    if (error) throw new Error('Не вдалося завантажити фото. Спробуй ще раз.');
    await updateProfile({ avatar_url: path });
    const url = c.toDataURL('image/jpeg', AVATAR_Q);
    photoUrls.set(`${AVATARS}/${path}`, Promise.resolve(url));
    keepMark(path, squareCanvas(c, MARK_PX).toDataURL('image/jpeg', MARK_Q));
    if (storedAvatar(prev) && prev !== path) forgetAvatarFile(prev);
    return url;
}
// No picture: initials everywhere (a Google picture goes too).
export async function removeAvatar() {
    const prev = (await me()).profile?.avatar_url;
    await updateProfile({ avatar_url: null });
    dropMark();
    if (storedAvatar(prev)) forgetAvatarFile(prev);
}
function forgetAvatarFile(path) {
    photoUrls.delete(`${AVATARS}/${path}`); avatarMarks.delete(path);
    Promise.resolve().then(() => client.storage.from(AVATARS).remove([path])).catch(() => {});
}
// Signing out on a shared device: no picture of the last person left behind
// (their homework photos neither).
export function forgetAvatar() { forgetPhotos(); avatarMarks.clear(); dropMark(); }

// Lessons, materials and homework of the student's group (teacher track).
export async function groupLessons(groupId) {
    // Undated lessons (lesson 0) first.
    return q(client.from('lessons').select('*').eq('group_id', groupId).order('starts_at', { nullsFirst: true }));
}
export async function groupInfo(groupId) {
    const [g, t] = await Promise.all([q(client.from('groups').select('*').eq('id', groupId).maybeSingle()),
        q(client.from('group_teachers').select('*').eq('group_id', groupId).is('left_at', null))]);
    // Staff read profiles; a student gets names only via my_teachers().
    let teachers = t.length ? await q(client.from('profiles').select('id,full_name,preferred_name').in('id', ids(t, 'teacher_id'))) : [];
    if (t.length && !teachers.length) teachers = (await q(client.rpc('my_teachers'))).filter(r => r.group_id === groupId).map(r => ({ id: r.teacher_id, full_name: r.full_name, preferred_name: r.preferred_name }));
    return { ...g, teachers };
}
export async function lessonMaterials(lessonId) {
    const own = await q(client.from('materials').select('*').eq('lesson_id', lessonId).order('created_at'));
    const links = await q(client.from('lesson_material_links').select('*').eq('lesson_id', lessonId));
    const linked = links.length ? await q(client.from('materials').select('*').in('id', ids(links, 'material_id'))) : [];
    return [...own, ...linked];
}
export async function myHomework() {
    return q(client.from('my_homework').select('*'));
}
export async function assignmentFull(id) {
    const a = await q(client.from('assignments').select('*').eq('id', id).maybeSingle());
    const problems = a ? await q(client.from('problems').select('id,index_no,statement,max_points,difficulty').eq('assignment_id', id).order('index_no')) : [];
    return { ...a, problems };
}
export async function mySubmission(assignmentId) {
    const uid = (await me()).user.id;
    const s = await q(client.from('submissions').select('*').eq('assignment_id', assignmentId).eq('student_id', uid).maybeSingle());
    const files = s ? await q(client.from('submission_files').select('*').eq('submission_id', s.id).order('page_index')) : [];
    return s ? { ...s, files } : null;
}
// Photos of the notebook → the private «submissions» bucket, then the rows.
// Hand in homework photos. Photos go up first (3 tries each); the work counts as
// handed in only when every photo is stored, and the server stamps the time.
// If the connection drops (outages are normal), the photos wait in a queue on
// the phone and go up when the network is back (flushHomeworkQueue).
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
const dataUrlToBlob = (u) => { const [h, b] = u.split(','); const bin = atob(b); const a = new Uint8Array(bin.length); for (let k = 0; k < bin.length; k++) a[k] = bin.charCodeAt(k); return new Blob([a], { type: (h.match(/data:([^;]+)/) || [])[1] || 'image/jpeg' }); };
async function retry(fn, tries = 3) {
    for (let k = 1; ; k++) {
        try { return await fn(); } catch (e) { if (k >= tries) throw e; await sleep(800 * k * k); }
    }
}
export async function submitHomework(assignmentId, photos, { capturedAt = new Date().toISOString() } = {}) {
    const uid = (await me()).user.id;
    let s = await retry(() => q(client.from('submissions').select('*').eq('assignment_id', assignmentId).eq('student_id', uid).maybeSingle()));
    if (!s) s = await retry(() => q(client.from('submissions').insert({ assignment_id: assignmentId, student_id: uid, status: 'draft', captured_at: capturedAt }).select('*').single()));
    // One submission (2026-10-09): work already handed in is final.
    if (s.status !== 'draft') throw Object.assign(new Error('Цю роботу вже здано'), { code: 'SUBMITTED' });
    const paths = [];
    let i = 0;
    for (const p of photos) {
        const path = `${s.id}/${Date.now()}-${i}.jpg`;
        await retry(async () => {
            const body = p.blob || (client.dev ? p.data : dataUrlToBlob(p.data));
            const { error } = await client.storage.from('submissions').upload(path, body, { contentType: 'image/jpeg', upsert: true });
            if (error) throw new Error(error.message);
        });
        paths.push(path); i++;
    }
    // All photos stored: replace the file list, then mark handed in.
    await retry(() => q(client.from('submission_files').delete().eq('submission_id', s.id)));
    for (const [k, path] of paths.entries()) {
        await retry(() => q(client.from('submission_files').insert({ submission_id: s.id, storage_path: path, page_index: k, mime_type: 'image/jpeg', bytes: photos[k].bytes || null })));
    }
    await retry(() => q(client.from('submissions').update({ status: 'submitted', captured_at: capturedAt }).eq('id', s.id)));
    kickHomeworkCheck(s.id);   // not awaited: the AI check runs on the server
    worldCache = null;
    logEvent('homework_submitted', { photos: photos.length, queued: !!photos.queued });
    return s;
}
// AI-track homework: my latest AI check of one work (my_homework_feedback,
// migration 20261011230000), or null. Points stay null until published.
export async function homeworkFeedback(assignmentId) {
    return q(client.from('my_homework_feedback').select('*').eq('assignment_id', assignmentId).maybeSingle());
}
// A page could not be read: the work opens again for more photos (files kept).
export async function reopenForReupload(submissionId) {
    worldCache = null;
    return q(client.rpc('reopen_for_reupload', { p_submission: submissionId }));
}
// The new photos of a reopened work go after its pages; finish_reupload closes it
// and queues a new check. Returns the check's id, or null (no AI check: a curator reads it).
export async function addReuploadPhotos(submissionId, photos) {
    const have = await retry(() => q(client.from('submission_files').select('page_index').eq('submission_id', submissionId)));
    const next = Math.max(-1, ...have.map(f => f.page_index ?? -1)) + 1;
    for (const [k, p] of photos.entries()) {
        const path = `${submissionId}/${Date.now()}-${k}.jpg`;
        try {
            await retry(async () => {
                const body = p.blob || (client.dev ? p.data : dataUrlToBlob(p.data));
                const { error } = await client.storage.from('submissions').upload(path, body, { contentType: 'image/jpeg', upsert: true });
                if (error) throw new Error(error.message);
            });
        } catch { throw new Error('Не вдалося завантажити фото. Спробуй ще раз.'); }
        await retry(() => q(client.from('submission_files').insert({ submission_id: submissionId, storage_path: path, page_index: next + k, mime_type: 'image/jpeg', bytes: p.bytes || null })));
    }
    const rid = await q(client.rpc('finish_reupload', { p_submission: submissionId }));
    kickHomeworkCheck(submissionId);   // not awaited: the AI check runs on the server
    worldCache = null;
    logEvent('homework_reuploaded', { photos: photos.length });
    return rid;
}
// Check a handed-in work now (cabinet-api POST /homework/kick) rather than on
// the next scheduled tick. true when the API took it; false otherwise, and in
// the in-browser dev database (no grader). Never throws.
export async function kickHomeworkCheck(submissionId) {
    if (!submissionId || isDev() || typeof client?.post !== 'function') return false;
    try { const r = await client.post('/homework/kick', { submission_id: submissionId }); return !!r?.ok; } catch { return false; }
}

// ── Offline queue (IndexedDB on the phone) ─────────────────────────────────
const QDB = 'apex-hw-queue';
function qdb() {
    return new Promise((res, rej) => { const r = indexedDB.open(QDB, 1); r.onupgradeneeded = () => r.result.createObjectStore('q', { keyPath: 'id' }); r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}
async function qop(mode, fn) { const db = await qdb(); return new Promise((res, rej) => { const tx = db.transaction('q', mode); const out = fn(tx.objectStore('q')); tx.oncomplete = () => res(out?.result ?? out); tx.onerror = () => rej(tx.error); }); }
export async function queueHomework(assignmentId, photos) {
    const uid = (await me()).user.id;
    const item = { id: `${uid}:${assignmentId}`, uid, assignmentId, photos: photos.map(p => ({ data: p.data, bytes: p.bytes })), capturedAt: new Date().toISOString() };
    await qop('readwrite', st => st.put(item));
    logEvent('homework_queued', { photos: photos.length });
}
export async function queuedHomework() {
    try { const uid = (await me())?.user.id; return (await qop('readonly', st => st.getAll())).filter(x => x.uid === uid); } catch { return []; }
}
let flushing = false;
export async function flushHomeworkQueue() {
    if (flushing || !navigator.onLine) return 0;
    flushing = true;
    let sent = 0;
    try {
        for (const item of await queuedHomework()) {
            try {
                const photos = item.photos; photos.queued = true;
                await submitHomework(item.assignmentId, photos, { capturedAt: item.capturedAt });
                await qop('readwrite', st => st.delete(item.id));
                sent++;
            } catch (err) {
                // Handed in from another device meanwhile: these photos can never go up.
                if (err.code === 'SUBMITTED') await qop('readwrite', st => st.delete(item.id));
                /* otherwise it stays queued; next time */
            }
        }
    } finally { flushing = false; }
    return sent;
}
// The homework's solution video: the database gives it once my work is handed in.
export async function homeworkVideo(assignmentId) { return q(client.rpc('homework_video', { p_assignment: assignmentId })); }
// The homework's solution in several videos, one per problem (migration
// 20261011000700): [{ problem, title, video, seconds }] once the student's own
// work is handed in (20261011120000), else []. The stored files' links are signed at once.
export async function homeworkVideos(assignmentId) {
    const v = await q(client.rpc('homework_videos', { p_assignment: assignmentId }));
    const list = Array.isArray(v) ? v : [];
    presignVideos(list.map(x => storedVideo(x.video)).filter(Boolean));
    return list;
}
// A topic file: a «materials» bucket path (signed here) or a ready URL / site path.
export const materialUrl = (v) => !v ? Promise.resolve(null) : /^(https?:\/\/|\/)/.test(v) ? Promise.resolve(v) : signedUrl('materials', v).catch(() => null);
// Photos (homework pages, profile pictures) never get a signed link, which works
// for whoever holds it and cannot be taken back (architecture review 2026-10-12):
// cabinet-api serves their bytes, checked on every fetch, and the page shows a
// blob: URL that lives only in this tab. The in-browser dev database signs instead.
const PHOTO_BUCKETS = new Set(['submissions', 'avatars']);
const photoUrls = new Map();   // bucket/path → Promise<string|null>
export function photoUrl(bucket, path) {
    const k = `${bucket}/${path}`;
    let hit = photoUrls.get(k);
    if (!hit) {
        const b = client.storage.from(bucket);
        hit = b.download ? b.download(path).then(r => r?.data ? URL.createObjectURL(r.data) : null, () => null)
            : b.createSignedUrl(path, 600).then(r => r?.data?.signedUrl || null, () => null);
        photoUrls.set(k, hit);
        hit.then(u => { if (!u && photoUrls.get(k) === hit) photoUrls.delete(k); });
        if (photoUrls.size > 200) {
            const [old, p] = photoUrls.entries().next().value;
            photoUrls.delete(old);
            p.then(u => { if (u?.startsWith('blob:')) URL.revokeObjectURL(u); });
        }
    }
    return hit;
}
function forgetPhotos() {
    for (const p of photoUrls.values()) p.then(u => { if (u?.startsWith('blob:')) URL.revokeObjectURL(u); });
    photoUrls.clear();
}
export async function signedUrl(bucket, path) {
    if (PHOTO_BUCKETS.has(bucket)) return photoUrl(bucket, path);
    const { data } = await client.storage.from(bucket).createSignedUrl(path, 600);
    return data?.signedUrl || null;
}
export async function myGrades() {
    return q(client.from('grades').select('*').eq('student_id', (await me()).user.id).order('created_at'));
}
export async function settingsRow() { return q(client.from('settings').select('*').limit(1).maybeSingle()); }
export async function mySettings() {
    const uid = (await me()).user.id;
    return (await q(client.from('student_settings').select('*').eq('student_id', uid).maybeSingle())) || { student_id: uid, board_see: false, board_show: false, group_optin: false, group_slots: [] };
}
export async function saveSettings(fields) {
    const uid = (await me()).user.id;
    return q(client.from('student_settings').upsert({ student_id: uid, ...fields, updated_at: new Date().toISOString() }, { onConflict: 'student_id' }));
}
export async function myMeetings() { return q(client.from('meetings').select('*').order('starts_at')); }

// ── Teacher ──────────────────────────────────────────────────────────────
// Admins and editors run every group (admin mode); a teacher sees their own.
export const runsGroups = (role) => role === 'admin' || role === 'editor';
// groups_read gives a teacher exactly the groups they teach now (teaches_group), so
// one read serves both: an admin's or editor's is every group.
export function myGroups() {
    return kept('myGroups', () => q(client.from('groups').select('*').eq('is_active', true).order('name')));
}
// The ids of a group's current students (the counts on the teacher's pages).
export const groupStudentIds = (groupId) => kept('students:' + groupId,
    async () => ids(await q(client.from('group_students').select('student_id').eq('group_id', groupId).is('left_at', null)), 'student_id'));
export async function roster(groupId) {
    const gs = await q(client.from('group_students').select('*').eq('group_id', groupId).is('left_at', null));
    if (!gs.length) return [];
    const [people, levels, prog] = await Promise.all([
        q(client.from('profiles').select('id,full_name,preferred_name,grade,email,status,consent_given_at').in('id', ids(gs, 'student_id'))),
        q(client.from('analytics_progress').select('*').in('student_id', ids(gs, 'student_id'))),
        q(client.from('analytics_practice').select('*').in('student_id', ids(gs, 'student_id'))),
    ]);
    const L = Object.fromEntries(levels.map(r => [r.student_id, r]));
    const Pr = Object.fromEntries(prog.map(r => [r.student_id, r]));
    return people.map(p => ({ ...p, level: L[p.id] ? +L[p.id].last_theta : null, firstLevel: L[p.id] ? +L[p.id].first_theta : null, practice: Pr[p.id] || null }))
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'uk'));
}
// A group's list (20261013160000_teacher_roster.sql): the management adds by email (an
// invitation when there is no account yet) and takes off (20261015000000); the teacher fixes a name.
export async function addStudentToGroup(groupId, email, fullName) {
    return q(client.rpc('teacher_add_student', { p_group: groupId, p_email: email, p_full_name: fullName }));
}
export async function renameStudent(groupId, studentId, fullName) {
    return q(client.rpc('teacher_rename_student', { p_group: groupId, p_student: studentId, p_full_name: fullName }));
}
export async function removeStudentFromGroup(groupId, studentId) {
    return q(client.rpc('teacher_remove_student', { p_group: groupId, p_student: studentId }));
}
// How each student got in (20261013190000_student_invitations.sql): the last
// invitation letter per student, {student_id → {invited_at, sent_at, failed}}.
export async function groupEntry(groupId) {
    const rows = await q(client.rpc('group_entry', { p_group: groupId }, { get: true }));
    return Object.fromEntries((rows || []).map(r => [r.student_id, r]));
}
// The addresses the mail provider will not deliver to (20261015050000_email_blocked.sql),
// for admins and editors; an admin asks for one to be let through (the tick asks the provider).
export const mailBlocked = () => q(client.rpc('mail_blocked_list'));
export const mailUnblock = (email) => q(client.rpc('mail_unblock', { p_email: email }));
export async function resendInvite(groupId, studentId) {
    return q(client.rpc('teacher_send_invite', { p_group: groupId, p_student: studentId }));
}
export async function fixStudentEmail(groupId, studentId, email) {
    return q(client.rpc('teacher_fix_email', { p_group: groupId, p_student: studentId, p_email: email }));
}
export async function lesson(id) { return q(client.from('lessons').select('*').eq('id', id).maybeSingle()); }
export async function updateLesson(id, fields) { return q(client.from('lessons').update(fields).eq('id', id)); }
export async function updateGroup(id, fields) { return q(client.from('groups').update(fields).eq('id', id)); }
export async function addMaterial(row) {
    return q(client.from('materials').insert({ ...row, created_by: (await me()).user.id }).select('*').single());
}
export async function removeMaterial(id) { return q(client.from('materials').delete().eq('id', id)); }
export async function linkMaterial(lessonId, materialId) {
    return q(client.from('lesson_material_links').upsert({ lesson_id: lessonId, material_id: materialId, added_by: (await me()).user.id }, { onConflict: 'lesson_id,material_id' }));
}
export async function unlinkMaterial(lessonId, materialId) {
    return q(client.from('lesson_material_links').delete().eq('lesson_id', lessonId).eq('material_id', materialId));
}
export async function materialBank(search = '') {
    let qy = client.from('materials').select('*').order('created_at', { ascending: false }).limit(200);
    if (search) qy = qy.ilike('title', `%${search}%`);
    return q(qy);
}
export async function topicSets(topicId) {
    const sets = await q(client.from('course_sets').select('*').eq('topic_id', topicId));
    const problems = sets.length ? await q(client.from('course_set_problems').select('*').in('set_id', ids(sets)).order('index_no')) : [];
    return sets.map(s => ({ ...s, problems: problems.filter(p => p.set_id === s.id) }));
}
export async function assignFromSet(setId, groupId, lessonId, due, publish) {
    return q(client.rpc('create_assignment_from_set', { p_set: setId, p_group: groupId, p_lesson: lessonId, p_due: due, p_publish: publish }));
}
export async function suggestedDue(lessonId) { return q(client.rpc('suggested_due_at', { p_lesson_id: lessonId })); }
// A lesson's optional problem set (extra, olympiad) made compulsory for the group,
// due with the homework, or optional again (20261013120000_lesson_problem_sets.sql).
export async function setAssignmentRequired(assignmentId, required) {
    return q(client.rpc('set_assignment_required', { p_assignment: assignmentId, p_required: required }));
}
// 102: the teacher opens a lesson to its students, or takes it back: 'lesson' its
// videos and problems with its sets' grades and the homework's solution at once
// (20261014150000; 'materials' or 'grades' one half). The new time, or null.
export async function setLessonOpen(lessonId, what, open) {
    return q(client.rpc('set_lesson_open', { p_lesson: lessonId, p_what: what, p_open: open }));
}
// What stands open in a group, for the teacher's pages (20261014210000_group_state.sql).
export async function groupState(groupId) { return q(client.rpc('group_state', { p_group: groupId }, { get: true })); }
// «Журнал групи» in one call (20261015000000_group_journal.sql): the group's attendance
// rows, its handed-in homework with the teacher's and AI Mind's points, each homework's max.
export async function groupJournal(groupId) { return q(client.rpc('group_journal', { p_group: groupId }, { get: true })); }
export async function groupAssignments(groupId) {
    return q(client.from('assignments').select('*').eq('group_id', groupId).order('created_at', { ascending: false }));
}
// ── Teacher: grading homework, problem by problem ──────────────────────────
// The total is never typed: problem_marks roll up into grades (trigger). The
// teacher's grade reaches the student once confirmed (confirm_grade); AI Mind's
// once published and past the deadline (RLS).
export async function assignmentWork(assignmentId) {
    const a = await q(client.from('assignments').select('*').eq('id', assignmentId).maybeSingle());
    if (!a) return null;
    const [problems, subs, roster] = await Promise.all([
        q(client.from('problems').select('id,index_no,max_points').eq('assignment_id', assignmentId).order('index_no')),
        // A draft is a hand-in still uploading (or abandoned): not the teacher's yet.
        q(client.from('submissions').select('*').eq('assignment_id', assignmentId)).then(r => r.filter(s => s.status !== 'draft')),
        q(client.from('group_students').select('student_id').eq('group_id', a.group_id).is('left_at', null)),
    ]);
    const sIds = subs.map(s => s.id);
    const [files, grades, marks, ai] = sIds.length ? await Promise.all([
        q(client.from('submission_files').select('submission_id').in('submission_id', sIds)),
        q(client.from('grades').select('*').in('submission_id', sIds)),
        q(client.from('problem_marks').select('submission_id,problem_id,points,graded_by').in('submission_id', sIds)),
        q(client.from('ai_reviews').select('submission_id,status,total_points,max_points,created_at').in('submission_id', sIds)),
    ]) : [[], [], [], []];
    const ids = [...new Set([...roster.map(r => r.student_id), ...subs.map(s => s.student_id)])];
    const people = ids.length ? await q(client.from('profiles').select('id,full_name,preferred_name').in('id', ids)) : [];
    return { a, problems, subs, roster: roster.map(r => r.student_id), files, grades, marks, ai, people };
}
export async function submissionForGrading(submissionId) {
    const s = await q(client.from('submissions').select('*').eq('id', submissionId).maybeSingle());
    if (!s) return null;
    const [a, problems, files, marks, grade, reviews, student] = await Promise.all([
        q(client.from('assignments').select('*').eq('id', s.assignment_id).maybeSingle()),
        q(client.from('problems').select('*').eq('assignment_id', s.assignment_id).order('index_no')),
        q(client.from('submission_files').select('*').eq('submission_id', s.id).order('page_index')),
        q(client.from('problem_marks').select('*').eq('submission_id', s.id)),
        q(client.from('grades').select('*').eq('submission_id', s.id).maybeSingle()),
        q(client.from('ai_reviews').select('*').eq('submission_id', s.id).eq('status', 'done').order('created_at', { ascending: false }).limit(1)),
        q(client.from('profiles').select('id,full_name,preferred_name').eq('id', s.student_id).maybeSingle()),
    ]);
    const ai = reviews[0] || null;
    // A problem's title is its lesson problem's (topic_homework_problems), when it has one.
    const tIds = problems.map(p => p.topic_problem_id).filter(Boolean);
    const [aiProblems, titles, urls] = await Promise.all([
        ai ? q(client.from('ai_problem_reviews').select('*').eq('ai_review_id', ai.id)) : [],
        tIds.length ? q(client.from('topic_homework_problems').select('id,title').in('id', tIds)).catch(() => []) : [],
        Promise.all(files.map(f => signedUrl('submissions', f.storage_path))),
    ]);
    const T = Object.fromEntries(titles.map(t => [t.id, t.title]));
    return { s, a, problems: problems.map(p => ({ ...p, title: T[p.topic_problem_id] || null })), files: files.map((f, i) => ({ ...f, url: urls[i] })), marks, grade, ai, aiProblems, student };
}
export async function saveMark(submissionId, problemId, mark) {
    const uid = (await me()).user.id;
    return q(client.from('problem_marks').upsert({ submission_id: submissionId, problem_id: problemId, ...mark, graded_by: uid, updated_at: new Date().toISOString() }, { onConflict: 'submission_id,problem_id' }));
}
// Stopgap for homework set as a PDF (no problems to mark one by one): one score
// for the whole work, written straight into grades. Only the given columns
// change, so re-saving a published grade keeps it published.
export async function saveWholeGrade(sub, a, points, comment, publish) {
    const row = { student_id: sub.student_id, group_id: a.group_id, assignment_id: a.id, submission_id: sub.id,
        points, max_points: a.max_points, comment, source: 'teacher', graded_by: (await me()).user.id };
    if (publish) { row.published_at = row.confirmed_at = new Date().toISOString(); row.confirmed_by = row.graded_by; }
    return q(client.from('grades').upsert(row, { onConflict: 'submission_id' }));
}
// What the homework's topic set and its solution video, for the grader.
export async function homeworkBrief(a) {
    const l = a.lesson_id ? await q(client.from('lessons').select('topic_id').eq('id', a.lesson_id).maybeSingle()) : null;
    if (!l?.topic_id) return null;
    const [t, sol] = await Promise.all([
        q(client.from('course_topics').select('id,title,homework_text,homework_pdf').eq('id', l.topic_id).maybeSingle()),
        q(client.from('topic_solutions').select('homework_video_url').eq('topic_id', l.topic_id).maybeSingle()),
    ]);
    return t && { ...t, video: sol?.homework_video_url || null };
}
// ── Staff: Програма → Заняття, read-only ───────────────────────────────────
// A subject's content is written through the course connector (cabinet/MCP_PLAN.md,
// decision 17), never in the cabinet: these only read. Staff read archived lessons
// and blocks too; the answers and hints only on their own subjects (RLS on
// topic_block_keys and topic_solutions: teaches_course()).
//
// The subjects a person sees there: every subject for admins and editors; a
// teacher's own, as teaches_course() decides (an open course_staff grant, or an
// open group of theirs on the subject now). RLS lets a teacher read every
// subject's lessons, so the scope is applied here.
export async function programCourses() {
    const m = await me();
    const courses = await q(client.from('courses').select('*').order('slug'));
    if (runsGroups(m.role)) return courses;
    const uid = m.user.id, today = new Date().toISOString().slice(0, 10);
    const [grants, gt] = await Promise.all([
        q(client.from('course_staff').select('course_id').eq('profile_id', uid).is('removed_at', null)),
        q(client.from('group_teachers').select('group_id').eq('teacher_id', uid).is('left_at', null)),
    ]);
    const gcs = gt.length ? await q(client.from('group_courses').select('course_id,ends_on').in('group_id', ids(gt, 'group_id'))) : [];
    const mine = new Set([...ids(grants, 'course_id'), ...gcs.filter(g => !g.ends_on || String(g.ends_on).slice(0, 10) >= today).map(g => g.course_id)]);
    return courses.filter(c => mine.has(c.id));
}
// Those subjects, each with all its lessons (archived ones last), their active
// blocks counted and the homework's solution video.
export async function programSubjects() {
    const courses = await programCourses();
    if (!courses.length) return [];
    const topics = await q(client.from('course_topics').select('*').in('course_id', ids(courses)).order('index_no'));
    const tids = ids(topics);
    const [blocks, sols] = tids.length ? await Promise.all([
        q(client.from('topic_blocks').select('topic_id,archived_at').in('topic_id', tids)),
        q(client.from('topic_solutions').select('topic_id,homework_video_url').in('topic_id', tids)).catch(() => []),
    ]) : [[], []];
    const nb = {}, sol = Object.fromEntries(sols.map(x => [x.topic_id, x.homework_video_url]));
    blocks.forEach(b => { if (!b.archived_at) nb[b.topic_id] = (nb[b.topic_id] || 0) + 1; });
    // Active lessons in programme order, then the archived ones.
    const order = (a, b) => (!!a.archived_at - !!b.archived_at) || a.index_no - b.index_no;
    return courses.map(course => ({ course, topics: topics.filter(t => t.course_id === course.id).sort(order)
        .map(t => ({ ...t, blocks: nb[t.id] || 0, solution_video: sol[t.id] || null })) }));
}
// One lesson as Програма → Заняття shows it: the topic, its solution video (one link, or one per problem), its
// blocks (archived too, for staff) and, where RLS lets the reader, their answers and hints.
export async function lessonContent(id) {
    const t = await q(client.from('course_topics').select('*').eq('id', id).maybeSingle());
    if (!t) return null;
    const [sol, blocks] = await Promise.all([
        q(client.from('topic_solutions').select('homework_video_url,homework_videos').eq('topic_id', id).maybeSingle()).catch(() => null),
        q(client.from('topic_blocks').select('id,n,title,video_url,body,statement,problem_title,inputs,archived_at').eq('topic_id', id).order('n').order('created_at')),
    ]);
    const keys = blocks.length ? await q(client.from('topic_block_keys').select('block_id,answers,hint').in('block_id', ids(blocks))).catch(() => []) : [];
    return { ...t, solution_video: sol?.homework_video_url || null, solution_videos: Array.isArray(sol?.homework_videos) ? sol.homework_videos : [], blocks, keys: Object.fromEntries(keys.map(k => [k.block_id, k])) };
}
export async function saveGradeComment(submissionId, comment) {
    return q(client.from('grades').update({ comment }).eq('submission_id', submissionId));
}
// The grading page saves a draft; «Підтвердити оцінку» shows the student the
// teacher's grade at once, «Змінити оцінку» takes it back (20261013180000_teacher_confirm.sql).
export async function confirmGrade(submissionId, comment) {
    return q(client.rpc('confirm_grade', { p_submission: submissionId, p_comment: comment }));
}
export async function unconfirmGrade(submissionId) {
    return q(client.rpc('unconfirm_grade', { p_submission: submissionId }));
}
// Whole-work grades (homework set as a PDF): students see them once published.
export async function publishGrades(assignmentId) {
    const at = new Date().toISOString(), by = (await me()).user.id;
    return q(client.from('grades').update({ published_at: at, confirmed_at: at, confirmed_by: by }).eq('assignment_id', assignmentId).is('published_at', null).select('id'));
}
// Student: my mark for one homework, problem by problem (visible after the deadline).
export async function myMarks(submissionId) {
    const [grade, marks] = await Promise.all([
        q(client.from('grades').select('*').eq('submission_id', submissionId).maybeSingle()),
        q(client.from('problem_marks').select('problem_id,points,verdict,comment,idea_without_rigour,featured').eq('submission_id', submissionId)),
    ]);
    return { grade, marks };
}

// How many of each student's works in the group this person has graded (a problem
// mark or a whole-work grade of theirs): the Standard asks that personal checks
// stay even across the course (§3), so the «ДЗ» list shows it.
export async function personalChecks(groupId, uid) {
    const as = await q(client.from('assignments').select('id').eq('group_id', groupId));
    if (!as.length) return {};
    const subs = await q(client.from('submissions').select('id,student_id').in('assignment_id', ids(as)));
    if (!subs.length) return {};
    const sIds = ids(subs);
    const [marks, whole] = await Promise.all([
        q(client.from('problem_marks').select('submission_id').in('submission_id', sIds).eq('graded_by', uid)),
        q(client.from('grades').select('submission_id').in('submission_id', sIds).eq('source', 'teacher').eq('graded_by', uid)),
    ]);
    const done = new Set([...ids(marks, 'submission_id'), ...ids(whole, 'submission_id')]), n = {};
    subs.forEach(x => { if (done.has(x.id)) n[x.student_id] = (n[x.student_id] || 0) + 1; });
    return n;
}
// An integrity violation found in a work (§3–4): one penalties row of kind
// 'integrity' per work, no AP until AP exists. note = null takes it back.
export async function integrityOf(submissionId) {
    return q(client.from('penalties').select('*').eq('submission_id', submissionId).eq('kind', 'integrity').maybeSingle());
}
export async function setIntegrity(sub, a, on, note = null) {
    if (!on) return q(client.from('penalties').delete().eq('submission_id', sub.id).eq('kind', 'integrity'));
    const row = { student_id: sub.student_id, group_id: a.group_id, lesson_id: a.lesson_id || null, submission_id: sub.id,
        kind: 'integrity', ap_delta: 0, note: note || null, issued_by: (await me()).user.id };
    const cur = await integrityOf(sub.id);
    return cur ? q(client.from('penalties').update({ note: row.note }).eq('id', cur.id)) : q(client.from('penalties').insert(row));
}

export async function attendanceOf(lessonId) { return q(client.from('attendance').select('*').eq('lesson_id', lessonId)); }
export async function saveAttendance(rows) {
    const uid = (await me()).user.id;
    return q(client.from('attendance').upsert(rows.map(r => ({ ...r, recorded_by: uid, recorded_at: new Date().toISOString() })), { onConflict: 'lesson_id,student_id' }));
}
export async function issuePenalty(studentId, groupId, lessonId, kind, note) {
    return q(client.from('penalties').insert({ student_id: studentId, group_id: groupId, lesson_id: lessonId, kind, note, issued_by: (await me()).user.id }));
}

// ── Admin ────────────────────────────────────────────────────────────────
export async function cohorts() { return q(client.from('cohorts').select('*').order('created_at', { ascending: false })); }
export async function updateCohort(id, fields) { return q(client.from('cohorts').update(fields).eq('id', id)); }
// student_track has a row per group, and a student can be in several (one per
// предмет: maths, North Star, English 101…). Programme numbers count each student
// once, by their group in `cohortId` (the admission cohort) when they have one.
export function onePerStudent(rows, cohortId) {
    const out = new Map();
    for (const r of rows) {
        const cur = out.get(r.student_id);
        if (!cur || (r.cohort_id === cohortId && cur.cohort_id !== cohortId)) out.set(r.student_id, r);
    }
    return [...out.values()];
}
export async function allGroups(cohortId) {
    let qy = client.from('groups').select('*').order('name');
    if (cohortId) qy = qy.eq('cohort_id', cohortId);
    return q(qy);
}
export async function createGroup(row) { return q(client.from('groups').insert(row).select('*').single()); }
export async function groupTeachers(groupIds) {
    if (!groupIds.length) return [];
    return q(client.from('group_teachers').select('*').in('group_id', groupIds).is('left_at', null));
}
// One lead per group. A teacher who leaves keeps their row, closed with left_at
// (who taught the group until when stays on record); the chosen teacher's row
// is added, or reopened when they taught it before (one row per pair).
export async function setGroupTeacher(groupId, teacherId) {
    const now = new Date().toISOString();
    let leave = client.from('group_teachers').update({ left_at: now }).eq('group_id', groupId).is('left_at', null);
    if (teacherId) leave = leave.neq('teacher_id', teacherId);
    await q(leave);
    if (!teacherId) return;
    const cur = await q(client.from('group_teachers').select('left_at').eq('group_id', groupId).eq('teacher_id', teacherId).maybeSingle());
    if (!cur) await q(client.from('group_teachers').insert({ group_id: groupId, teacher_id: teacherId, is_lead: true }));
    else if (cur.left_at) await q(client.from('group_teachers').update({ left_at: null, joined_at: now, is_lead: true }).eq('group_id', groupId).eq('teacher_id', teacherId));
}
export async function people(role) {
    let qy = client.from('profiles').select('*').order('full_name');
    if (role) qy = qy.eq('role', role);
    return q(qy);
}
// Admins invite any role, editors teachers and students (invite_person decides).
export async function invite(email, fullName, role) {
    return q(client.rpc('invite_person', { p_email: email, p_full_name: fullName, p_role: role }));
}
// ── Selection (admin): applicants, interview scores, decisions, enrolment ──
export async function selectionList(cohortId) {
    const [rows, scores] = await Promise.all([
        q(client.from('applicant_scores').select('*').eq('cohort_id', cohortId)),
        q(client.from('interview_scores').select('applicant_id,rater_id')),
    ]);
    return { rows, scores };
}
export async function applicantFull(id) {
    const [a, scores] = await Promise.all([
        q(client.from('applicant_scores').select('*').eq('id', id).maybeSingle()),
        q(client.from('interview_scores').select('*').eq('applicant_id', id)),
    ]);
    const raters = scores.length ? await q(client.from('profiles').select('id,full_name,preferred_name').in('id', scores.map(x => x.rater_id))) : [];
    return a ? { a, scores: scores.map(x => ({ ...x, rater: raters.find(r => r.id === x.rater_id) })) } : null;
}
export async function saveApplicant(id, fields) {
    return q(client.from('applicants').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id));
}
export async function saveMyScore(applicantId, fields) {
    const uid = (await me()).user.id;
    return q(client.from('interview_scores').upsert({ applicant_id: applicantId, rater_id: uid, ...fields, updated_at: new Date().toISOString() }, { onConflict: 'applicant_id,rater_id' }));
}
export async function enrolApplicants(cohortId) { return q(client.rpc('enrol_applicants', { p_cohort: cohortId })); }
export async function applications(cohortId) { return q(client.from('applications').select('*').eq('cohort_id', cohortId)); }
export async function placementsOf(studentIds) {
    if (!studentIds.length) return [];
    return q(client.from('placements').select('id,student_id,theta,minutes,finished_at,answers').in('student_id', studentIds));
}
export async function saveAllocation(rows) {
    return q(client.from('applications').upsert(rows.map(r => ({ ...r, updated_at: new Date().toISOString() })), { onConflict: 'id' }));
}
export async function publishAllocation(cohortId) { return q(client.rpc('publish_allocation', { p_cohort: cohortId })); }
export async function moveStudent(studentId, toGroup) { return q(client.rpc('move_student_to_group', { p_student: studentId, p_to_group: toGroup })); }
// What the AI costs, for «Витрати ШІ»: cabinet-api POST /admin/spend (admin_ai_spend, the
// service's limits and the OpenRouter key's own numbers). Without the service (dev), the
// database's numbers alone.
export async function aiSpend() {
    try {
        const r = await client.post('/admin/spend', {});
        if (r?.ok) return (await r.json()).data;
    } catch { /* the database's numbers below */ }
    return { ...(await q(client.rpc('admin_ai_spend', { p_days: 31 }))), limits: null, openrouter: null };
}

export async function analytics() {
    const [activity, topics, groupTopic, progress, practice, ai, tracks, prep] = await Promise.all([
        q(client.from('analytics_activity_daily').select('*').order('day')),
        q(client.from('analytics_topic_status').select('*').order('index_no')),
        q(client.from('analytics_group_topic').select('*')),
        q(client.from('analytics_progress').select('*')),
        q(client.from('analytics_practice').select('*')),
        q(client.from('analytics_ai_daily').select('*').order('day')),
        q(client.from('student_track').select('*')),
        q(client.from('prep_progress').select('student_id,topic_id,explanation,game,cards_reviewed,recall_tries,ai_explains')),
    ]);
    return { activity, topics, groupTopic, progress, practice, ai, tracks, prep };
}
// Launch view: funnel from first-party events, consent, weekly active.
export async function launchStats() {
    const since = new Date(Date.now() - 28 * 864e5).toISOString();
    const [funnel, studs, consents, act] = await Promise.all([
        q(client.from('analytics_funnel').select('*')),
        q(client.from('profiles').select('id,full_name,preferred_name,consent_given_at,status').eq('role', 'student')),
        q(client.from('parental_consents').select('student_id,token,given_at,created_at')),
        q(client.from('activity').select('user_id,created_at').gte('created_at', since)),
    ]);
    return { funnel, studs, consents, act };
}
export async function recentActivity(days = 14) {
    const since = new Date(Date.now() - days * 864e5).toISOString();
    return q(client.from('activity').select('user_id,created_at,path').gte('created_at', since));
}
// ── Admin: AI homework checks (migration 20261011230000_homework_grading.sql) ──
// Newest first. `flag` is matched here: the query layer has no array operators.
export async function aiReviewsList({ status, flag, limit = 200 } = {}) {
    let qy = client.from('admin_ai_reviews').select('*').order('created_at', { ascending: false });
    if (status) qy = qy.eq('status', status);
    if (!flag) qy = qy.limit(limit);
    const rows = await q(qy);
    return flag ? rows.filter(r => (r.flags || []).includes(flag)).slice(0, limit) : rows;
}
// One check with its model calls, per-problem results, the work's photos
// (signed for 30 min) and the marking schemes it was checked against.
export async function aiReviewDetail(reviewId) {
    const review = await q(client.from('admin_ai_reviews').select('*').eq('review_id', reviewId).maybeSingle());
    if (!review) return null;
    const [steps, problems, files] = await Promise.all([
        q(client.from('ai_review_steps').select('*').eq('ai_review_id', reviewId).order('seq').order('created_at')),
        q(client.from('ai_problem_reviews').select('*').eq('ai_review_id', reviewId).order('index_no')),
        q(client.from('submission_files').select('storage_path,page_index').eq('submission_id', review.submission_id).order('page_index')),
    ]);
    const pids = ids(problems, 'problem_id');
    const links = pids.length ? await q(client.from('problems').select('id,topic_problem_id').in('id', pids)) : [];
    const tids = ids(links, 'topic_problem_id');
    const paths = files.map(f => f.storage_path);
    const [rows, signed] = await Promise.all([
        tids.length ? q(client.from('topic_homework_problems').select('*').in('id', tids)) : [],
        Promise.all(paths.map(p => photoUrl('submissions', p).then(signedUrl => ({ path: p, signedUrl })))),
    ]);
    return { review, steps, problems,
        files: files.map(f => ({ path: f.storage_path, page_index: f.page_index, url: signed.find(x => x.path === f.storage_path)?.signedUrl || null })),
        schemes: Object.fromEntries(rows.map(r => [r.id, r])) };
}
// Every homework problem's marking scheme with its subject and lesson, by
// subject, lesson code and problem number.
export async function homeworkSchemes() {
    const rows = await q(client.from('topic_homework_problems').select('*'));
    const topics = rows.length ? await q(client.from('course_topics').select('id,code,title,course_id').in('id', ids(rows, 'topic_id'))) : [];
    const courses = topics.length ? await q(client.from('courses').select('id,slug').in('id', ids(topics, 'course_id'))) : [];
    const T = Object.fromEntries(topics.map(t => [t.id, t])), C = Object.fromEntries(courses.map(c => [c.id, c]));
    return rows.map(r => ({ ...r, course_slug: C[T[r.topic_id]?.course_id]?.slug || null, topic_code: T[r.topic_id]?.code || null, topic_title: T[r.topic_id]?.title || null }))
        .sort((a, b) => String(a.course_slug).localeCompare(String(b.course_slug)) || String(a.topic_code).localeCompare(String(b.topic_code), 'uk', { numeric: true })
            || ['homework', 'extra', 'olympiad'].indexOf(a.problem_set) - ['homework', 'extra', 'olympiad'].indexOf(b.problem_set) || a.index_no - b.index_no);
}
// Approves every problem whose content matches `sha` (both subjects share it); → how many.
export async function approveHomeworkScheme(id, sha) { return q(client.rpc('approve_homework_scheme', { p_id: id, p_sha: sha })); }
// A fresh check of one work (optionally on another model), started at once; → the new check's id.
export async function requestRegrade(submissionId, model) {
    const id = await q(client.rpc('request_ai_regrade', { p_submission: submissionId, p_model: model || null }));
    kickHomeworkCheck(submissionId);
    return id;
}
// A held check (quality gates failed) published as the AI marked it; the student is emailed.
export async function publishHeldReview(reviewId) { return q(client.rpc('admin_publish_ai_review', { p_review: reviewId })); }
// The emails a check sent or queued (admins' alert, the student's «перевірено»); [] before the outbox exists.
export async function reviewMails(reviewId) {
    const rows = await q(client.from('email_outbox').select('*').eq('ref_review', reviewId)).catch(() => []);
    return rows.sort((a, b) => String(a.created_at || a.sent_at || '').localeCompare(String(b.created_at || b.sent_at || '')));
}

// ── Before / after the lesson: preparation, flashcards, recall, AI explain ──
export async function logPrep(kind, code, { score = null, detail = null } = {}) {
    logEvent('prep_' + kind, { topic: code, score });
    worldCache = null;
    const { byCode } = await yearOne();
    return client.from('prep_events').insert({ student_id: (await me()).user.id, topic_id: byCode[code]?.id || null, kind, score, detail });
}
export async function myPrep() {
    const uid = (await me()).user.id;
    const { byId } = await yearOne();
    const [rows, events] = await Promise.all([
        q(client.from('prep_progress').select('*').eq('student_id', uid)),
        q(client.from('prep_events').select('*').eq('student_id', uid).order('created_at')),
    ]);
    const byCode = {};
    rows.forEach(r => { const t = byId[r.topic_id]; if (t) byCode[t.code] = r; });
    return { byCode, events };
}
export async function myCards() {
    return q(client.from('flashcard_state').select('*').eq('student_id', (await me()).user.id));
}
export async function saveCard(row) {
    const { byCode } = await yearOne();
    const code = row.card_key.split(':')[0];
    worldCache = null;
    return q(client.from('flashcard_state').upsert({ ...row, student_id: (await me()).user.id, topic_id: byCode[code]?.id, updated_at: new Date().toISOString() }, { onConflict: 'student_id,card_key' }));
}
// Teacher: who prepared for a topic, and who asked the AI to explain it.
export async function prepOf(studentIds, topicId) {
    if (!studentIds.length) return { rows: [], asks: [] };
    let a = client.from('prep_progress').select('*').in('student_id', studentIds);
    let b = client.from('prep_events').select('*').eq('kind', 'ai_explain').in('student_id', studentIds).order('created_at', { ascending: false });
    if (topicId) { a = a.eq('topic_id', topicId); b = b.eq('topic_id', topicId); }
    const [rows, asks] = await Promise.all([q(a), q(b)]);
    return { rows, asks };
}
